import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const Sidebar = ({ activeTab, setActiveTab }) => {
  const { user, logout } = useAuth();
  const [bluebancBalance, setBluebancBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);

  const fetchBluebancBalance = async () => {
    setLoadingBalance(true);
    try {
      const response = await fetch('/api/bluebanc/balance', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setBluebancBalance(data);
      }
    } catch (err) {
      console.error('Error fetching Bluebanc balance:', err);
    } finally {
      setLoadingBalance(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchBluebancBalance();
      const interval = setInterval(fetchBluebancBalance, 60000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const allTabs = [
    { id: 'reports', name: 'NMI Reports', icon: '📊', roles: ['superadmin'] },
    { id: 'friendlies-us', name: 'Traffic Distribution', icon: '🇺🇸', roles: ['superadmin'] },
    { id: 'jobs', name: 'Jobs', icon: '⚡', roles: ['superadmin'] },
    { id: 'cardholders', name: 'CardHolders', icon: '💳', roles: ['superadmin'] },
    { id: 'auto-friendlies', name: 'Auto Friendlies', icon: '🤖', roles: ['superadmin'] },
    { id: 'auto-friendlies-ss', name: 'Auto Friendlies SS', icon: '🛒', roles: ['superadmin'] },
    { id: 'gateways', name: 'Gateways', icon: '⚙️', roles: ['superadmin'] }
  ];

  const tabs = allTabs.filter(tab => tab.roles.includes(user?.role));

  const getRoleBadge = (role) => {
    const badges = {
      superadmin: { text: 'Superadmin', color: 'bg-purple-100 text-purple-800' },
      full_access: { text: 'Full Access', color: 'bg-blue-100 text-blue-800' },
      restricted: { text: 'Support', color: 'bg-orange-100 text-orange-800' }
    };
    return badges[role] || badges.restricted;
  };

  const badge = getRoleBadge(user?.role);

  return (
    <aside className="w-64 bg-white border-r border-gray-200 flex flex-col">
      {/* Logo */}
      <div className="p-6 border-b border-gray-200">
        <h1 className="text-2xl font-bold text-gray-900">Traffic Management</h1>
        <p className="text-sm text-gray-500 mt-1">NMI Analytics & Traffic</p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4">
        <ul className="space-y-2">
          {tabs.map((tab) => (
            <li key={tab.id}>
              <button
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center px-4 py-3 rounded-lg text-left transition-smooth ${
                  activeTab === tab.id
                    ? 'bg-primary-50 text-primary-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className="text-xl mr-3">{tab.icon}</span>
                <span>{tab.name}</span>
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {/* Bluebanc Balance */}
      {bluebancBalance && (
        <div className="p-4 border-t border-gray-200">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-600">Bluebanc Balance</span>
              <button 
                onClick={fetchBluebancBalance}
                disabled={loadingBalance}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                {loadingBalance ? '⏳' : '🔄'}
              </button>
            </div>
            <div className="text-2xl font-bold text-indigo-600">
              ${bluebancBalance.totalAvailable.toFixed(2)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Available Balance
            </div>
          </div>
        </div>
      )}

      {/* User Info & Logout */}
      <div className="p-4 border-t border-gray-200 space-y-3">
        <div className="px-2">
          <p className="text-sm font-medium text-gray-900">{user?.username}</p>
          <span className={`inline-block mt-1 px-2 py-1 text-xs font-medium rounded-full ${badge.color}`}>
            {badge.text}
          </span>
        </div>
        <button
          onClick={logout}
          className="w-full px-4 py-2 text-sm text-red-600 border border-red-600 rounded-lg hover:bg-red-50 transition-smooth"
        >
          Logout
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
